import React, { useMemo, useState, useEffect } from 'react'
import SimpleAccordion from '../../../ui_reusable/SimpleAccordion'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  FormGrid,
  InputsWrapper,
  ParameterLabel,
  ParametersInfo,
  ParameterValue,
  RowTitle,
  StyledInput,
} from './styles/ParametersForm.styles'
import {
  ErrorMessage,
  Label,
  LabelFaded,
} from '../../../ui_reusable/styles/CreateInputUtils.styles'
import { FormSelectPicker } from '../../../ui_reusable/StandardSelectPickers'
import BuildModelTimeframeSelector from './BuildModelTimeframeSelector'
import {
  aggregationMethodOptions,
  intervalOptions,
} from '../../../../utils/dropdowns'
import {
  ImpactArtifactRequest,
  ProjectSolution,
  ProjectSolutionDriver,
} from '@/api/generated/models'
import { useModelParameters } from './hooks/useModelParameters'
import ModelNameEdit from './shared/ModelNameEdit'
import StandardComboBox, {
  SearchableItem,
} from '../../../ui_reusable/StandardComboBox'

type ImpactParametersFormProps = {
  projectSolutionDrivers: ProjectSolutionDriver[]
  projectSolution: ProjectSolution
}

const ImpactParametersForm = ({
  projectSolutionDrivers,
  projectSolution,
}: ImpactParametersFormProps) => {
  const methods = useFormContext<ImpactArtifactRequest>()
  const {
    watch,
    setValue,
    formState: { errors },
  } = methods

  const { handleDependentDriverChange, dateRange } = useModelParameters(
    projectSolutionDrivers,
  )

  const dependentDriverId = useWatch({
    control: methods.control,
    name: 'dependent_driver_id',
  })

  const dependentDriver = useMemo(() => {
    if (!dependentDriverId) return null
    const projectSolutionDriver = projectSolutionDrivers?.find(
      (psd) => psd.driver?.id === dependentDriverId,
    )
    return projectSolutionDriver?.driver ?? null
  }, [dependentDriverId, projectSolutionDrivers])

  const availableIntervalOptions = useMemo(() => {
    if (!dependentDriver) return []
    switch (dependentDriver.interval) {
      case 'daily':
        return intervalOptions
      case 'weekly':
        return intervalOptions.filter((opt) =>
          ['monthly', 'weekly'].includes(opt.value),
        )
      default:
        return []
    }
  }, [dependentDriver])

  const searchableDrivers: SearchableItem[] = useMemo(() => {
    return (
      projectSolutionDrivers?.map((projectSolutionDriver) => ({
        id: String(projectSolutionDriver.driver?.id ?? ''),
        name: projectSolutionDriver.driver?.name ?? '',
      })) ?? []
    )
  }, [projectSolutionDrivers])

  const [selectedDriverItems, setSelectedDriverItems] = useState<
    SearchableItem[]
  >([])

  useEffect(() => {
    if (dependentDriverId) {
      const matchingDriver = projectSolutionDrivers?.find(
        (psd) => psd.driver?.id === dependentDriverId,
      )
      if (matchingDriver) {
        setSelectedDriverItems([
          {
            id: String(matchingDriver.driver?.id ?? ''),
            name: matchingDriver.driver?.name ?? '',
          },
        ])
      }
    }
  }, [dependentDriverId, projectSolutionDrivers])

  const handleDriverComboChange = (items: SearchableItem[]) => {
    setSelectedDriverItems(items)
    if (items && items.length > 0) {
      const selectedId = Number(items[0].id)
      const selectedDriver = projectSolutionDrivers?.find(
        (psd) => psd.driver?.id === selectedId,
      )
      if (selectedDriver?.driver?.id) {
        handleDependentDriverChange(selectedDriver.driver.id)
      }
    } else {
      handleDependentDriverChange(null)
    }
  }

  return (
    <SimpleAccordion.Panel header="Set Parameters" eventKey={'2'}>
      <FormGrid>
        <ModelNameEdit />
        <RowTitle>I want to:</RowTitle>
        <InputsWrapper>
          <StyledInput>
            <LabelFaded htmlFor="business_goal">
              Select business goal
            </LabelFaded>
            <FormSelectPicker
              id="business_goal"
              data={[
                { label: 'Increase', value: 'increase' },
                { label: 'Decrease', value: 'decrease' },
              ]}
              value={watch('parameters.business_goal')}
              onChange={(value) =>
                setValue('parameters.business_goal', String(value || ''))
              }
            />
            <ErrorMessage>
              {errors.parameters?.business_goal?.message || ''}
            </ErrorMessage>
          </StyledInput>
          <StyledInput>
            <Label htmlFor="dependent_driver">Set outcome variable</Label>
            <StandardComboBox
              data={searchableDrivers}
              onChange={handleDriverComboChange}
              placeholder="Select outcome variable..."
              maxSelected={1}
              alwaysShowList={true}
              valueList={selectedDriverItems}
              singleSelection={true}
              style={{ width: '20rem' }}
              allowCreate={false}
            />
            <ErrorMessage>
              {errors.dependent_driver_id?.message || ''}
            </ErrorMessage>
          </StyledInput>
          {dependentDriver && dependentDriver?.interval !== 'monthly' && (
            <>
              <StyledInput>
                <Label htmlFor="dependent_driver">Granularity</Label>
                <FormSelectPicker
                  id="frequency"
                  data={availableIntervalOptions}
                  value={watch('parameters.model_data_frequency')}
                  onChange={(value) =>
                    setValue(
                      'parameters.model_data_frequency',
                      String(value || ''),
                    )
                  }
                  style={{ width: '11rem' }}
                />
                <ErrorMessage>
                  {errors.parameters?.model_data_frequency?.message || ''}
                </ErrorMessage>
              </StyledInput>
              <StyledInput>
                <Label htmlFor="aggregation_method">Aggregation method</Label>
                <FormSelectPicker
                  id="aggregation_method"
                  data={aggregationMethodOptions}
                  value={watch('parameters.aggregation_method')}
                  onChange={(value) =>
                    setValue(
                      'parameters.aggregation_method',
                      String(value || ''),
                    )
                  }
                  style={{ width: '11rem' }}
                />
                <ErrorMessage>
                  {errors.parameters?.aggregation_method?.message || ''}
                </ErrorMessage>
              </StyledInput>
            </>
          )}
        </InputsWrapper>
        <BuildModelTimeframeSelector
          timeframe={watch('timeframe')}
          onTimeframeChange={(value) =>
            setValue('timeframe', value, { shouldValidate: true })
          }
          daterange={dateRange}
          dependentDriver={dependentDriver}
          error={errors.timeframe}
        />
      </FormGrid>
      <ParametersInfo>
        <ParameterLabel>
          Observations in timeframe:{' '}
          <ParameterValue>
            {dependentDriver?.total_observations || 0}
          </ParameterValue>
        </ParameterLabel>
        <ParameterLabel>
          Granularity:{' '}
          <ParameterValue>
            {watch('parameters.model_data_frequency') ||
              dependentDriver?.interval ||
              'N/A'}
          </ParameterValue>
        </ParameterLabel>
        <ParameterLabel>
          Model tuning:{' '}
          <ParameterValue>
            {projectSolution.model_library === 'xgboost_optimized'
              ? 'Optuna'
              : 'Auto-optimization'}
          </ParameterValue>
        </ParameterLabel>
      </ParametersInfo>
    </SimpleAccordion.Panel>
  )
}

export default ImpactParametersForm
